import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import {
  generateRandomString,
  generateCodeChallenge,
  startPKCEFlow as sharedStartPKCEFlow,
  exchangeCodeForToken as sharedExchangeCodeForToken,
  refreshAccessToken as sharedRefreshAccessToken,
  getOriginalEncodedUrl,
  setPrimaryLanguage,
  getPrimaryLanguage,
  type SSOConfig,
} from '@pfizer/shared/sso';
import { CLIENT_ID, AUTH_URL, ACCESS_TOKEN_URL } from '../constants/ssoURLs';
import { GET_USER_INFO } from '../constants/apiRoutes';
import { httpClient, setSSOFunctions } from '../utils/httpClient';
import { APIUserInfoResponse, ListUserType } from '../types/Users';
import { isReloggingAfterTimeoutSegment } from '../store/globalStore/stateSegments/sessionSegment';
import store from '../store/markettingContentGeneration';
import Logger from '../utils/logger';

// SSO config for this app
const ssoConfig: SSOConfig = {
  clientId: CLIENT_ID,
  authUrl: AUTH_URL,
  accessTokenUrl: ACCESS_TOKEN_URL,
};

// Re-export shared functions
export {
  generateRandomString,
  generateCodeChallenge,
  getOriginalEncodedUrl,
  setPrimaryLanguage,
  getPrimaryLanguage,
};

// Wrapper functions with app config
export const startPKCEFlow = (): Promise<void> =>
  sharedStartPKCEFlow(ssoConfig);
export const exchangeCodeForToken = (code: string): Promise<void> =>
  sharedExchangeCodeForToken(code, ssoConfig);
export const refreshAccessToken = (refreshToken: string): Promise<void> =>
  sharedRefreshAccessToken(refreshToken, ssoConfig);

/**
 * Fetches user information from the AWS v1/user/info.
 * It stores the retrieved user's given name in the session storage.
 * @returns {Promise<void>} - Returns a promise that resolves with nothing if successful, otherwise throws an error.
 * @throws Will throw an error if the access token is missing or there's an issue fetching the user information.
 */
export const fetchUserInfo = async (): Promise<ListUserType | null> => {
  try {
    const response = await httpClient.get<APIUserInfoResponse>(GET_USER_INFO);
    const { data, success } = response as APIUserInfoResponse;

    let givenName = 'User';
    let role = 'GENERAL';
    if (success && data) {
      let fullName = '';
      const firstName = get(data, 'user.firstName', '');
      const lastName = get(data, 'user.lastName', '');
      if (!isEmpty(firstName)) {
        fullName += firstName;
      }
      if (!isEmpty(lastName)) {
        fullName += ` ${lastName}`;
      }

      if (!isEmpty(fullName)) {
        givenName = fullName;
      }
      role = get(data, 'user.roles[0].roleCode', 'GENERAL');
    }

    setPrimaryLanguage('en');

    if (givenName && role) {
      sessionStorage.setItem('pfz-cggenai-user-givenname', givenName);
      sessionStorage.setItem('pfz-cggenai-user-role', role);
    } else {
      Logger.error(
        new Error(
          'Given name or user role is not available in the user info response.'
        )
      );
    }

    if (success && data) {
      return data.user;
    }
    return null;
  } catch (error) {
    Logger.error(
      new Error(
        get(error, 'response.data.message', 'Error fetching user information')
      )
    );

    return null;
  }
};

/**
 * Sets the relogging after timeout flag
 * @param isRelogging - Boolean indicating if user is relogging after timeout
 */
export const setReloggingAfterTimeout = (isRelogging: boolean): void => {
  store.set(isReloggingAfterTimeoutSegment, isRelogging);
};

/**
 * Gets the current relogging after timeout status
 * @returns Boolean indicating if user is currently relogging after timeout
 */
export const getReloggingAfterTimeoutStatus = (): boolean => {
  return store.get(isReloggingAfterTimeoutSegment);
};

/**
 * Clears the relogging after timeout flag
 */
export const clearReloggingAfterTimeout = (): void => {
  store.set(isReloggingAfterTimeoutSegment, false);
};

/**
 * Initiates SSO login with timeout flag for session timeout scenarios
 * This will set the relogging flag and start the authentication flow
 * After authentication, the callback should check for the timeout flag
 */
export const initiateTimeoutRelogin = (): void => {
  // Set the relogging flag
  setReloggingAfterTimeout(true);

  // Store a flag in session storage to indicate this is a timeout relogin
  sessionStorage.setItem('pfz-cggenai-timeout-relogin', 'true');

  // Start the normal PKCE flow
  startPKCEFlow();
};

// Register the SSO functions with httpClient for automatic retry mechanism
setSSOFunctions(refreshAccessToken, startPKCEFlow);
